import { useState, useEffect, useCallback, useContext } from 'react';
import { getQuizsAll } from '../firebaseFunction';
import { useDebounce } from './useDebounce';
import { MainContext } from '../context';

export const useQuizState = () => {
  const { selectedTab } = useContext(MainContext);
  const [quizData, setQuizData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const ITEMS_PER_PAGE = 10;

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const fetchQuizzes = useCallback(async (pageNumber = 1) => {
    try {
      if (pageNumber === 1) {
        setLoading(true);
      }
      const response = await getQuizsAll();
      
      if (!response || !response.allQuiz) {
        throw new Error('No quiz data received');
      }

      let filteredQuizzes = response.allQuiz;

      // تطبيق الفلترة حسب التبويب
      if (selectedTab) {
        filteredQuizzes = filteredQuizzes.filter(quiz => quiz.tab === selectedTab);
      }

      // تطبيق البحث
      if (debouncedSearchTerm) {
        filteredQuizzes = filteredQuizzes.filter(quiz => 
          quiz.title.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          quiz.description.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
        );
      }

      // تطبيق الصفحات
      const startIndex = (pageNumber - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      const paginatedQuizzes = filteredQuizzes.slice(startIndex, endIndex);
      
      setHasMore(endIndex < filteredQuizzes.length);
      setQuizData(prevData => pageNumber === 1 ? paginatedQuizzes : [...prevData, ...paginatedQuizzes]);
      setError(null);
    } catch (err) {
      console.error('Error fetching quizzes:', err);
      setError('Failed to load quizzes. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [selectedTab, debouncedSearchTerm]);

  useEffect(() => {
    setPage(1);
    const timer = setTimeout(() => {
      fetchQuizzes(1);
    }, 0);
    return () => clearTimeout(timer);
  }, [selectedTab, debouncedSearchTerm]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchQuizzes(nextPage);
    }
  }, [loading, hasMore, page, fetchQuizzes]);

  return {
    quizData,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    hasMore,
    loadMore,
    refreshQuizzes: () => fetchQuizzes(1)
  };
}; 